<template>
  <!-- animated fleet assistant avatar. four variants, each with a quiet idle
       loop and a louder attention loop (active=true) used when the assistant
       is trying to tell the user something:
         bubble  — the classic chat-bubble glyph (default)
         cloud   — the fleet integrations cloud-with-arrows brand mark
         stapler — the initek stapler-bus mascot (clippy energy)
         nova    — a glowing futuristic energy core (gradient orb + orbits)
         plasma  — pure shimmering living energy (churning hue-shifting mass)
       sizing is left to the host context (same .fa-chat-svg rules the old
       inline glyph used); colors are currentColor for bubble/cloud and
       self-contained for stapler/nova/plasma. -->
  <svg
    class="fa-avatar"
    :class="['fa-avatar--' + safeVariant, { 'is-active': active }]"
    :viewBox="viewBox"
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
  >
    <!-- BUBBLE — canonical chat-bubble + typing dots. idle: the dots run one
         quick typing wave every few seconds. active: continuous fast wave
         while the whole bubble rocks side to side. -->
    <g v-if="safeVariant === 'bubble'" class="fa-av-bubble">
      <path
        d="M6 3h12a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3h-4l-5 4v-4H6a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3z"
        :fill="inverted ? '#fff' : 'none'"
        :stroke="inverted ? '#000' : 'currentColor'"
        :stroke-width="inverted ? 1.5 : 1.75"
        stroke-linejoin="round"
      />
      <circle class="fa-av-dot fa-av-dot--1" cx="8.5" cy="10.25" r="1.25" :fill="inverted ? '#000' : 'currentColor'"/>
      <circle class="fa-av-dot fa-av-dot--2" cx="12" cy="10.25" r="1.25" :fill="inverted ? '#000' : 'currentColor'"/>
      <circle class="fa-av-dot fa-av-dot--3" cx="15.5" cy="10.25" r="1.25" :fill="inverted ? '#000' : 'currentColor'"/>
    </g>

    <!-- CLOUD — the brand mark, geometry lifted from the canonical LogoMark
         svg (stroke bumped 8 → 10 so it holds up at icon sizes). idle: the
         two arrows drift toward their pointed ends, like data trickling
         through. active: the whole cloud bounces while the arrows shuttle
         hard in both directions. -->
    <g
      v-else-if="safeVariant === 'cloud'"
      class="fa-av-cloud"
      stroke="currentColor"
      stroke-width="10"
      stroke-linecap="round"
      stroke-linejoin="round"
      fill="none"
    >
      <path d="M36 120 C18 120 4 106 4 88 C4 74 14 62 28 57 C26 42 36 30 52 30 C58 30 64 32 68 36 C76 18 92 8 110 8 C138 8 160 30 162 57 C180 60 192 76 192 92 C192 108 178 120 162 120 Z"/>
      <path class="fa-av-arrow fa-av-arrow--right" d="M65 64 H133 M116 48 L133 64 L116 80"/>
      <path class="fa-av-arrow fa-av-arrow--left" d="M133 92 H65 M82 76 L65 92 L82 108"/>
    </g>

    <!-- STAPLER — red shuttle-bus mascot with a stapler soul, facing left
         toward the page. the silhouette is a tall boxy shuttle (window band,
         front face panel, bumper, big wheels); the stapler survives as the
         silver staple-track beltline, the rear hinge cap, and the fact that
         the whole top half chomps. idle: breathing bob, synced blinks, and
         an occasional tiny jaw nibble. active: bounces in place, jaw
         chatters, a gloved arm pops out and waves, and alert sparks flash
         overhead. -->
    <g v-else-if="safeVariant === 'stapler'" class="fa-av-stapler">
      <!-- waving arm: tucked behind the body, shown only while active -->
      <g class="fa-av-arm">
        <path d="M10 40.5 Q5 36.5 4.2 30" fill="none" stroke="#1a1f2e" stroke-width="1.7" stroke-linecap="round"/>
        <circle cx="4.2" cy="28.5" r="3" fill="#fff" stroke="#1a1f2e" stroke-width="1.1"/>
      </g>
      <!-- wheels -->
      <circle cx="18" cy="50.5" r="6.5" fill="#263238"/>
      <circle cx="18" cy="50.5" r="2.8" fill="#cfd8dc"/>
      <circle cx="46" cy="50.5" r="6.5" fill="#263238"/>
      <circle cx="46" cy="50.5" r="2.8" fill="#cfd8dc"/>
      <!-- staple track peeking out at the beltline where the jaw meets the body -->
      <rect x="9.5" y="32" width="43" height="4" rx="1.6" fill="#cfd8dc"/>
      <!-- lower body (stapler base): side panel, badge, headlight, bumper -->
      <rect x="8" y="34" width="48" height="14.5" rx="3" fill="#e02b2b"/>
      <ellipse cx="39" cy="41" rx="8.5" ry="3.4" fill="#fff"/>
      <text x="39" y="42.2" text-anchor="middle" font-size="3" font-weight="700" letter-spacing="0.2" fill="#c62828" font-family="'Helvetica Neue', Arial, sans-serif">INITEK</text>
      <circle cx="10.3" cy="38.5" r="1.7" fill="#ffd54f"/>
      <rect x="7" y="46.5" width="50" height="4" rx="2" fill="#b71c1c"/>
      <!-- upper deck (the jaw) — roof, window band, face. swings on the rear hinge -->
      <g class="fa-av-jaw">
        <rect x="8" y="15" width="48" height="18" rx="6" fill="#e02b2b"/>
        <rect x="11" y="16.5" width="13" height="2.2" rx="1.1" fill="#fff" opacity="0.35"/>
        <!-- passenger window band behind the front face panel -->
        <rect x="18.5" y="19" width="8.5" height="8.5" rx="2" fill="#28354f"/>
        <rect x="29.5" y="19" width="8.5" height="8.5" rx="2" fill="#28354f"/>
        <rect x="40.5" y="19" width="8.5" height="8.5" rx="2" fill="#28354f"/>
        <!-- googly eyes with rims, popped over the roofline at the front -->
        <g class="fa-av-eye">
          <circle cx="13.8" cy="10.5" r="5.2" fill="#fff" stroke="#263238" stroke-width="1.4"/>
          <circle cx="13.1" cy="11.3" r="2.1" fill="#1a1f2e"/>
          <circle cx="12.3" cy="9.7" r="0.7" fill="#fff"/>
        </g>
        <g class="fa-av-eye">
          <circle cx="24.8" cy="10.5" r="5.2" fill="#fff" stroke="#263238" stroke-width="1.4"/>
          <circle cx="24.1" cy="11.3" r="2.1" fill="#1a1f2e"/>
          <circle cx="23.3" cy="9.7" r="0.7" fill="#fff"/>
        </g>
        <path d="M19 10.2 L19.6 10.2" stroke="#263238" stroke-width="1.4" stroke-linecap="round"/>
        <!-- smile on the red front face panel, under the eyes -->
        <path d="M10.5 27.5 Q13.5 30 17 28" fill="none" stroke="#7f1d1d" stroke-width="1.3" stroke-linecap="round"/>
        <!-- necktie, knotted at the chin so it swings with the jaw -->
        <path d="M13.8 33 L16.8 33 L15.3 35.2 Z" fill="#1f3a93"/>
        <path d="M15.3 35 L17.4 41 L15.3 43 L13.2 41 Z" fill="#27429e"/>
      </g>
      <!-- hinge cap drawn last so it covers the joint while the jaw swings -->
      <rect x="50.5" y="29.5" width="7" height="7.5" rx="2.2" fill="#9c1212"/>
      <!-- attention sparks, shown only while active -->
      <g class="fa-av-alert" stroke="#f59e0b" stroke-width="1.7" stroke-linecap="round" fill="none">
        <path d="M8.5 6 L6.5 2.5"/>
        <path d="M19.3 3.5 L19.3 0.5"/>
        <path d="M30 6 L32 2.5"/>
      </g>
    </g>

    <!-- NOVA — a glowing energy core. a gradient orb breathes inside two
         tilted, slowly counter-spinning rings, circled by orbiting sparks and
         the odd twinkle. futuristic, with a little wonder. idle: lazy spin,
         breathing core, staggered twinkles. active: the rings whip around, the
         core flares, and an energy ring pulses outward. self-contained color;
         gradient ids are per-instance so multiple avatars can coexist. -->
    <g v-else-if="safeVariant === 'nova'" class="fa-av-nova">
      <defs>
        <radialGradient :id="'fa-nova-core-' + uid" cx="38%" cy="34%" r="68%">
          <stop offset="0%" stop-color="#ffffff"/>
          <stop offset="34%" stop-color="#9becff"/>
          <stop offset="68%" stop-color="#2f9bff"/>
          <stop offset="100%" stop-color="#7c5cff"/>
        </radialGradient>
        <radialGradient :id="'fa-nova-halo-' + uid" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="#8fd6ff" stop-opacity="0.55"/>
          <stop offset="60%" stop-color="#8fb6ff" stop-opacity="0.18"/>
          <stop offset="100%" stop-color="#8f9cff" stop-opacity="0"/>
        </radialGradient>
      </defs>

      <!-- soft glow behind everything -->
      <circle class="fa-av-nova-halo" cx="32" cy="32" r="26" :fill="'url(#fa-nova-halo-' + uid + ')'"/>

      <!-- energy ring, pops outward only while active -->
      <circle class="fa-av-nova-burst" cx="32" cy="32" r="13" fill="none" stroke="#9becff" stroke-width="1.4"/>

      <!-- two tilted rings, spun as one system -->
      <g class="fa-av-nova-rings" fill="none">
        <ellipse cx="32" cy="32" rx="23" ry="9" transform="rotate(-24 32 32)" stroke="#7fd4ff" stroke-width="1.5" stroke-opacity="0.6"/>
        <ellipse cx="32" cy="32" rx="23" ry="9" transform="rotate(32 32 32)" stroke="#b79cff" stroke-width="1.5" stroke-opacity="0.55"/>
      </g>

      <!-- orbiting sparks, counter-spinning against the rings -->
      <g class="fa-av-nova-orbit">
        <circle cx="53" cy="32" r="2.1" fill="#ffffff"/>
        <circle cx="21.5" cy="50.2" r="1.7" fill="#bfefff"/>
        <circle cx="21.5" cy="13.8" r="1.5" fill="#cfd2ff"/>
      </g>

      <!-- the core: breathing gradient orb with a bright highlight -->
      <g class="fa-av-nova-core">
        <circle cx="32" cy="32" r="9.5" :fill="'url(#fa-nova-core-' + uid + ')'"/>
        <circle cx="28.8" cy="28.6" r="2.4" fill="#ffffff" opacity="0.9"/>
      </g>

      <!-- twinkles scattered near the rings -->
      <g class="fa-av-nova-twinkles" fill="#ffffff">
        <path class="fa-av-nova-twinkle fa-av-nova-twinkle--1" d="M48 11 Q48.7 13.3 51 14 Q48.7 14.7 48 17 Q47.3 14.7 45 14 Q47.3 13.3 48 11 Z"/>
        <path class="fa-av-nova-twinkle fa-av-nova-twinkle--2" d="M14 42.6 Q14.6 44.4 16.4 45 Q14.6 45.6 14 47.4 Q13.4 45.6 11.6 45 Q13.4 44.4 14 42.6 Z"/>
        <path class="fa-av-nova-twinkle fa-av-nova-twinkle--3" d="M50 45 Q50.5 46.5 52 47 Q50.5 47.5 50 49 Q49.5 47.5 48 47 Q49.5 46.5 50 45 Z"/>
      </g>
    </g>

    <!-- PLASMA — pure living energy. layered organic blobs churn at different
         speeds and directions so the silhouette never settles (living/breathing),
         a white-hot core pulses like a heartbeat, embers drift off the mass, and
         the whole thing drifts through vibrant hues (shimmer). idle: slow churn +
         slow hue drift. active: everything speeds up and burns brighter. colors
         are self-contained; gradient ids are per-instance so multiple avatars
         can coexist. -->
    <g v-else class="fa-av-plasma">
      <defs>
        <radialGradient :id="'fa-plasma-core-' + uid" cx="42%" cy="38%" r="65%">
          <stop offset="0%" stop-color="#ffffff"/>
          <stop offset="30%" stop-color="#9bf6ff"/>
          <stop offset="62%" stop-color="#ff3df2"/>
          <stop offset="100%" stop-color="#7a2cff"/>
        </radialGradient>
        <radialGradient :id="'fa-plasma-halo-' + uid" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="#ff5fe1" stop-opacity="0.36"/>
          <stop offset="45%" stop-color="#9b6cff" stop-opacity="0.15"/>
          <stop offset="100%" stop-color="#6cc8ff" stop-opacity="0"/>
        </radialGradient>
        <!-- softens the energy cloud so it reads diffuse, not as crisp shapes.
             generous region so the blur doesn't clip as the blobs churn. -->
        <filter :id="'fa-plasma-soften-' + uid" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2.4"/>
        </filter>
      </defs>

      <!-- breathing glow -->
      <circle class="fa-av-plasma-halo" cx="32" cy="32" r="30" :fill="'url(#fa-plasma-halo-' + uid + ')'"/>

      <!-- the energy mass: breathes as one, blobs churn within -->
      <g class="fa-av-plasma-mass">
        <!-- the diffuse cloud — softened + translucent so the core reads as a
             sphere inside a haze rather than a stack of hard shapes -->
        <g class="fa-av-plasma-cloud" :filter="'url(#fa-plasma-soften-' + uid + ')'">
          <!-- wispy tendrils: stroked arcs curling around the core, each layer
               spinning at its own speed so the wisps swirl. blur + low opacity
               feather the round-capped ends into smoke. -->
          <g class="fa-av-plasma-blob fa-av-plasma-blob--a" fill="none" stroke="#ff3df2" stroke-width="4.5" stroke-linecap="round" opacity="0.55">
            <path d="M13 37 C13 23 24 14 38 15 C44 15.5 48 18 51 22"/>
            <path d="M51 38 C51 49 41 53 31 50"/>
          </g>
          <g class="fa-av-plasma-blob fa-av-plasma-blob--b" fill="none" stroke="#22d3ee" stroke-width="3.8" stroke-linecap="round" opacity="0.45">
            <path d="M17 45 C12 37 14 26 22 20"/>
            <path d="M45 19 C51 25 52 36 46 44"/>
          </g>
          <g class="fa-av-plasma-blob fa-av-plasma-blob--c" fill="none" stroke="#ff6bd1" stroke-width="3.2" stroke-linecap="round" opacity="0.4">
            <path d="M25 18 C31 15 39 16 44 21"/>
            <path d="M43 46 C36 50 27 49 22 44"/>
          </g>
        </g>
        <!-- white-hot core + glint stay crisp inside the haze -->
        <circle class="fa-av-plasma-core" cx="32" cy="31" r="9" :fill="'url(#fa-plasma-core-' + uid + ')'"/>
        <circle class="fa-av-plasma-glint" cx="29" cy="28" r="2.4" fill="#ffffff" opacity="0.9"/>
      </g>

      <!-- embers drifting off the mass -->
      <g class="fa-av-plasma-sparks" fill="#ffffff">
        <circle class="fa-av-plasma-spark fa-av-plasma-spark--1" cx="46" cy="20" r="1.5"/>
        <circle class="fa-av-plasma-spark fa-av-plasma-spark--2" cx="18" cy="22" r="1.2"/>
        <circle class="fa-av-plasma-spark fa-av-plasma-spark--3" cx="44" cy="44" r="1.3"/>
        <circle class="fa-av-plasma-spark fa-av-plasma-spark--4" cx="20" cy="45" r="1.1"/>
      </g>
    </g>
  </svg>
</template>

<script>
export default {
  name: 'fa_avatar',
  props: {
    // 'bubble' | 'cloud' | 'stapler' | 'nova' | 'plasma'
    // — unknown values fall back to bubble
    variant: {
      type: String,
      default: 'bubble',
    },
    // attention mode: plays the louder look-at-me animation
    active: {
      type: Boolean,
      default: false,
    },
    // bubble-only: white-fill/black-stroke styling for the panel title bar
    inverted: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    safeVariant() {
      const v = this.variant
      const known = ['cloud', 'stapler', 'nova', 'plasma']
      return known.indexOf(v) >= 0 ? v : 'bubble'
    },
    // per-instance suffix so the nova gradients don't collide when several
    // avatars render on the same page (launcher toggle + header + settings).
    uid() {
      return this._uid
    },
    viewBox() {
      // square boxes so all variants sit in the same icon footprint. the
      // cloud's native art is 200x128, so its box is padded vertically to
      // center it.
      if (this.safeVariant === 'cloud') return '-2 -36 200 200'
      // stapler, nova, and plasma all live in a 64×64 box
      if (this.safeVariant !== 'bubble') return '0 0 64 64'
      return '0 0 24 24'
    },
  },
}
</script>

<style scoped>
/* bounces and sparks poke slightly past the viewBox on purpose */
.fa-avatar {
  overflow: visible;
}

/* ---------- bubble ---------- */

/* idle: one quick typing wave every 7s, dots staggered so it ripples */
.fa-av-dot {
  animation: fa-av-dot-idle 7s ease-in-out infinite;
}
.fa-av-dot--2 { animation-delay: 0.12s; }
.fa-av-dot--3 { animation-delay: 0.24s; }

@keyframes fa-av-dot-idle {
  0%, 86%, 94%, 100% { transform: translateY(0); }
  90%                { transform: translateY(-1.6px); }
}

/* active: continuous fast typing while the bubble rocks */
.is-active .fa-av-dot {
  animation: fa-av-dot-active 0.75s ease-in-out infinite;
}
.is-active .fa-av-dot--2 { animation-delay: 0.1s; }
.is-active .fa-av-dot--3 { animation-delay: 0.2s; }

@keyframes fa-av-dot-active {
  0%, 100% { transform: translateY(0); }
  35%      { transform: translateY(-2.4px); }
  60%      { transform: translateY(0.4px); }
}

.is-active .fa-av-bubble {
  transform-box: fill-box;
  transform-origin: 50% 55%;
  animation: fa-av-bubble-rock 1.4s ease-in-out infinite;
}

@keyframes fa-av-bubble-rock {
  0%, 100% { transform: rotate(0deg); }
  25%      { transform: rotate(-5deg); }
  75%      { transform: rotate(5deg); }
}

/* ---------- cloud ---------- */

/* idle: arrows drift toward their points, like data trickling through.
   distances are in the cloud's 200-unit space, so they render small. */
.fa-av-arrow--right { animation: fa-av-arrow-right-idle 3.6s ease-in-out infinite; }
.fa-av-arrow--left  { animation: fa-av-arrow-left-idle 3.6s ease-in-out infinite; }

@keyframes fa-av-arrow-right-idle {
  0%, 100% { transform: translateX(0); }
  50%      { transform: translateX(10px); }
}
@keyframes fa-av-arrow-left-idle {
  0%, 100% { transform: translateX(0); }
  50%      { transform: translateX(-10px); }
}

/* active: hard fast shuttling + the whole mark bounces */
.is-active .fa-av-arrow--right { animation: fa-av-arrow-right-active 0.7s ease-in-out infinite; }
.is-active .fa-av-arrow--left  { animation: fa-av-arrow-left-active 0.7s ease-in-out infinite; }

@keyframes fa-av-arrow-right-active {
  0%, 100% { transform: translateX(-6px); }
  50%      { transform: translateX(18px); }
}
@keyframes fa-av-arrow-left-active {
  0%, 100% { transform: translateX(6px); }
  50%      { transform: translateX(-18px); }
}

.is-active .fa-av-cloud {
  animation: fa-av-cloud-bounce 0.9s ease-in-out infinite;
}

@keyframes fa-av-cloud-bounce {
  0%, 100% { transform: translateY(0); }
  40%      { transform: translateY(-14px); }
}

/* ---------- stapler ---------- */

/* idle: gentle breathing bob */
.fa-av-stapler {
  animation: fa-av-stapler-breathe 3.4s ease-in-out infinite;
}

@keyframes fa-av-stapler-breathe {
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-1.2px); }
}

/* idle: occasional tiny jaw nibble. positive rotation lifts the front
   (left) end around the rear hinge. */
.fa-av-jaw {
  transform-box: view-box;
  transform-origin: 52px 33px;
  animation: fa-av-jaw-nibble 6.5s ease-in-out infinite;
}

@keyframes fa-av-jaw-nibble {
  0%, 78%, 92%, 100% { transform: rotate(0deg); }
  82%, 88%           { transform: rotate(2.6deg); }
  85%                { transform: rotate(0.8deg); }
}

/* idle: synced blink */
.fa-av-eye {
  transform-box: fill-box;
  transform-origin: 50% 75%;
  animation: fa-av-eye-blink 4.6s ease-in-out infinite;
}

@keyframes fa-av-eye-blink {
  0%, 92%, 100% { transform: scaleY(1); }
  95%, 97%      { transform: scaleY(0.08); }
}

.fa-av-arm,
.fa-av-alert {
  opacity: 0;
}

/* active: bounce with a small landing squash */
.is-active .fa-av-stapler {
  transform-box: fill-box;
  transform-origin: 50% 100%;
  animation: fa-av-stapler-bounce 0.9s ease-in-out infinite;
}

@keyframes fa-av-stapler-bounce {
  0%, 100% { transform: translateY(0) scaleY(1); }
  30%      { transform: translateY(-4.5px) scaleY(1.02); }
  55%      { transform: translateY(0) scaleY(0.97); }
  70%      { transform: translateY(-1.5px) scaleY(1); }
}

/* active: jaw chatters, eyes go wide (blink suspended) */
.is-active .fa-av-jaw {
  animation: fa-av-jaw-chatter 0.45s ease-in-out infinite;
}

@keyframes fa-av-jaw-chatter {
  0%, 100% { transform: rotate(0deg); }
  45%      { transform: rotate(9deg); }
}

.is-active .fa-av-eye {
  animation: none;
}

/* active: arm pops out and waves */
.is-active .fa-av-arm {
  opacity: 1;
  transform-box: view-box;
  transform-origin: 10px 40.5px;
  animation: fa-av-arm-wave 0.7s ease-in-out infinite;
}

@keyframes fa-av-arm-wave {
  0%, 100% { transform: rotate(4deg); }
  50%      { transform: rotate(-22deg); }
}

/* active: alert sparks flash overhead */
.is-active .fa-av-alert {
  animation: fa-av-alert-blink 0.8s ease-in-out infinite;
}

@keyframes fa-av-alert-blink {
  0%, 100% { opacity: 0; }
  40%, 70% { opacity: 1; }
}

/* ---------- nova ---------- */

/* idle: the two rings drift slowly clockwise as one tilted system */
.fa-av-nova-rings {
  transform-box: view-box;
  transform-origin: 32px 32px;
  animation: fa-av-nova-spin 9s linear infinite;
}

/* idle: the sparks orbit the other way for a layered, alive feel */
.fa-av-nova-orbit {
  transform-box: view-box;
  transform-origin: 32px 32px;
  animation: fa-av-nova-spin-rev 7s linear infinite;
}

@keyframes fa-av-nova-spin {
  to { transform: rotate(360deg); }
}
@keyframes fa-av-nova-spin-rev {
  to { transform: rotate(-360deg); }
}

/* idle: the core breathes */
.fa-av-nova-core {
  transform-box: fill-box;
  transform-origin: 50% 50%;
  animation: fa-av-nova-breathe 3.6s ease-in-out infinite;
}

@keyframes fa-av-nova-breathe {
  0%, 100% { transform: scale(1); }
  50%      { transform: scale(1.07); }
}

/* idle: the halo glows in and out, softly out of phase with the core */
.fa-av-nova-halo {
  transform-box: fill-box;
  transform-origin: 50% 50%;
  animation: fa-av-nova-halo 3.6s ease-in-out infinite;
}

@keyframes fa-av-nova-halo {
  0%, 100% { opacity: 0.7; transform: scale(0.96); }
  50%      { opacity: 1;   transform: scale(1.04); }
}

/* idle: twinkles wink on a stagger */
.fa-av-nova-twinkle {
  transform-box: fill-box;
  transform-origin: 50% 50%;
  animation: fa-av-nova-twinkle 3.2s ease-in-out infinite;
}
.fa-av-nova-twinkle--2 { animation-delay: 1.05s; }
.fa-av-nova-twinkle--3 { animation-delay: 2.1s; }

@keyframes fa-av-nova-twinkle {
  0%, 70%, 100% { opacity: 0.25; transform: scale(0.4); }
  85%           { opacity: 1;    transform: scale(1); }
}

/* the active burst ring is hidden until attention mode */
.fa-av-nova-burst {
  opacity: 0;
}

/* active: rings and sparks whip around */
.is-active .fa-av-nova-rings { animation-duration: 2.4s; }
.is-active .fa-av-nova-orbit { animation-duration: 1.8s; }

/* active: the core flares brighter and bigger */
.is-active .fa-av-nova-core {
  animation: fa-av-nova-flare 0.9s ease-in-out infinite;
}

@keyframes fa-av-nova-flare {
  0%, 100% { transform: scale(1); }
  50%      { transform: scale(1.15); }
}

/* active: the halo pulses harder */
.is-active .fa-av-nova-halo {
  animation: fa-av-nova-halo-active 0.9s ease-in-out infinite;
}

@keyframes fa-av-nova-halo-active {
  0%, 100% { opacity: 0.8; transform: scale(0.98); }
  50%      { opacity: 1;   transform: scale(1.12); }
}

/* active: an energy ring pulses outward from the core */
.is-active .fa-av-nova-burst {
  transform-box: fill-box;
  transform-origin: 50% 50%;
  animation: fa-av-nova-burst 1.2s ease-out infinite;
}

@keyframes fa-av-nova-burst {
  0%   { opacity: 0.9; transform: scale(0.7); }
  100% { opacity: 0;   transform: scale(1.8); }
}

/* active: twinkles wink faster */
.is-active .fa-av-nova-twinkle { animation-duration: 1.1s; }

/* ---------- plasma (pure living energy) ---------- */

/* the whole variant group drifts through vibrant hues — the shimmer.
   hue-rotate leaves the white-hot center bright (no saturation to rotate)
   while the saturated edges cycle the spectrum. */
.fa-av-plasma {
  animation: fa-av-plasma-hue 9s linear infinite;
}

@keyframes fa-av-plasma-hue {
  0%   { filter: hue-rotate(0deg); }
  100% { filter: hue-rotate(360deg); }
}

/* idle: the mass breathes as one */
.fa-av-plasma-mass {
  transform-box: view-box;
  transform-origin: 32px 32px;
  animation: fa-av-plasma-breathe 4s ease-in-out infinite;
}

@keyframes fa-av-plasma-breathe {
  0%, 100% { transform: scale(1); }
  50%      { transform: scale(1.06); }
}

/* idle: blobs churn at different speeds + directions so the outline never
   settles — the living, breathing read */
.fa-av-plasma-blob {
  transform-box: view-box;
  transform-origin: 32px 32px;
}
.fa-av-plasma-blob--a { animation: fa-av-plasma-spin-cw 12s linear infinite; }
.fa-av-plasma-blob--b { animation: fa-av-plasma-spin-ccw 16s linear infinite; }
.fa-av-plasma-blob--c { animation: fa-av-plasma-spin-cw 9s linear infinite; }

@keyframes fa-av-plasma-spin-cw  { to { transform: rotate(360deg); } }
@keyframes fa-av-plasma-spin-ccw { to { transform: rotate(-360deg); } }

/* idle: the white-hot core pulses like a heartbeat */
.fa-av-plasma-core {
  transform-box: fill-box;
  transform-origin: 50% 50%;
  animation: fa-av-plasma-core 2.4s ease-in-out infinite;
}

@keyframes fa-av-plasma-core {
  0%, 100% { transform: scale(0.92); opacity: 0.92; }
  50%      { transform: scale(1.08); opacity: 1; }
}

/* idle: the glow breathes with the mass */
.fa-av-plasma-halo {
  transform-box: fill-box;
  transform-origin: 50% 50%;
  animation: fa-av-plasma-halo 4s ease-in-out infinite;
}

@keyframes fa-av-plasma-halo {
  0%, 100% { opacity: 0.6; transform: scale(0.95); }
  50%      { opacity: 1;   transform: scale(1.05); }
}

/* idle: embers drift off, fading in and out on a stagger */
.fa-av-plasma-spark {
  transform-box: fill-box;
  transform-origin: 50% 50%;
  animation: fa-av-plasma-spark 3.2s ease-in-out infinite;
}
.fa-av-plasma-spark--2 { animation-delay: 0.8s; }
.fa-av-plasma-spark--3 { animation-delay: 1.6s; }
.fa-av-plasma-spark--4 { animation-delay: 2.4s; }

@keyframes fa-av-plasma-spark {
  0%, 100% { opacity: 0; transform: scale(0.4); }
  50%      { opacity: 0.9; transform: scale(1); }
}

/* active: everything speeds up and burns brighter */
.is-active .fa-av-plasma { animation-duration: 2.4s; }
.is-active .fa-av-plasma-mass { animation-duration: 1.8s; }
.is-active .fa-av-plasma-blob--a { animation-duration: 5s; }
.is-active .fa-av-plasma-blob--b { animation-duration: 6.5s; }
.is-active .fa-av-plasma-blob--c { animation-duration: 4s; }
.is-active .fa-av-plasma-core { animation-duration: 0.9s; }
.is-active .fa-av-plasma-halo { animation-duration: 1.8s; }
.is-active .fa-av-plasma-spark { animation-duration: 1.3s; }

/* ---------- reduced motion ---------- */

/* keep everything still; attention falls back to a soft opacity pulse plus
   the stapler's static raised arm + sparks so the cue survives. */
@media (prefers-reduced-motion: reduce) {
  .fa-av-dot,
  .fa-av-bubble,
  .fa-av-arrow--right,
  .fa-av-arrow--left,
  .fa-av-cloud,
  .fa-av-stapler,
  .fa-av-jaw,
  .fa-av-eye,
  .fa-av-arm,
  .fa-av-alert,
  .fa-av-nova-rings,
  .fa-av-nova-orbit,
  .fa-av-nova-core,
  .fa-av-nova-halo,
  .fa-av-nova-twinkle,
  .fa-av-nova-burst,
  .fa-av-plasma,
  .fa-av-plasma-mass,
  .fa-av-plasma-blob,
  .fa-av-plasma-core,
  .fa-av-plasma-halo,
  .fa-av-plasma-spark {
    animation: none !important;
  }
  .fa-avatar.is-active {
    animation: fa-av-soft-pulse 2.4s ease-in-out infinite;
  }
  .is-active .fa-av-arm,
  .is-active .fa-av-alert {
    opacity: 1;
  }
}

@keyframes fa-av-soft-pulse {
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.55; }
}
</style>
